import { JsonObject, JsonValue } from "@/domains/common/types";
import { objectModule } from "@/modules/object";
import { windowModule } from "@/modules/window";

const MAX_DETAIL_ARRAY_ITEMS = 5;
const MAX_DETAIL_DEPTH = 3;
const MAX_DETAIL_KEYS = 25;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const isUrlLikeDetailKey = (key: string) => {
  const normalizedKey = key.toLowerCase();

  return (
    normalizedKey === "src" ||
    normalizedKey === "url" ||
    normalizedKey === "href" ||
    normalizedKey === "uri" ||
    normalizedKey.endsWith("src") ||
    normalizedKey.endsWith("srcs") ||
    normalizedKey.endsWith("url") ||
    normalizedKey.endsWith("urls") ||
    normalizedKey.endsWith("href") ||
    normalizedKey.endsWith("hrefs") ||
    normalizedKey.endsWith("uri") ||
    normalizedKey.endsWith("uris")
  );
};

const compactEditorOperationLogValue = ({
  depth,
  key,
  value,
}: {
  depth: number;
  key: string;
  value: unknown;
}): JsonValue => {
  if (typeof value === "string" && isUrlLikeDetailKey(key)) {
    return windowModule.compactUrlForLogging(value) as JsonObject;
  }

  if (Array.isArray(value)) {
    return {
      itemCount: value.length,
      items: value.slice(0, MAX_DETAIL_ARRAY_ITEMS).map(item =>
        compactEditorOperationLogValue({
          depth: depth + 1,
          key,
          value: item,
        })
      ),
      omittedItemCount: Math.max(0, value.length - MAX_DETAIL_ARRAY_ITEMS),
    };
  }

  if (isRecord(value)) {
    if (depth >= MAX_DETAIL_DEPTH) {
      return objectModule.summarizeForLogging(value);
    }

    const entries = Object.entries(value);
    const result: JsonObject = {};

    for (const [childKey, childValue] of entries.slice(0, MAX_DETAIL_KEYS)) {
      result[childKey] = compactEditorOperationLogValue({
        depth: depth + 1,
        key: childKey,
        value: childValue,
      });
    }

    if (entries.length > MAX_DETAIL_KEYS) {
      result.omittedKeyCount = entries.length - MAX_DETAIL_KEYS;
    }

    return result;
  }

  return objectModule.compactForLogging(value) as JsonValue;
};

export const compactEditorOperationLogDetails = (details: Record<string, unknown>): JsonObject => {
  return compactEditorOperationLogValue({
    depth: 0,
    key: "details",
    value: details,
  }) as JsonObject;
};
